import { useEffect, useRef, useState, useCallback } from 'react';
import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision';
import { Hand, Mic, AlertCircle, RefreshCw, Activity, Sparkles } from 'lucide-react';
import { detectHandShape, drawHand } from '../lib/handUtils';

const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';
const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';

const VIDEO_CONSTRAINTS = {
  audio: false,
  video: {
    facingMode: 'user',
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 30, max: 30 },
  },
};

export default function POC() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const fpsBufferRef = useRef([]); // timestamps (ms) of detection frames
  const lastVideoTimeRef = useRef(-1);
  const initStartedRef = useRef(false); // guard against React.StrictMode double-mount

  const [camStatus, setCamStatus] = useState('idle'); // idle|requesting|granted|denied|error
  const [camError, setCamError] = useState('');
  const [mpReady, setMpReady] = useState(false);
  const [mpError, setMpError] = useState('');
  const [fps, setFps] = useState(0);
  const [handState, setHandState] = useState('none'); // open|closed|none
  const [speaking, setSpeaking] = useState(false);
  const [ttsSupported, setTtsSupported] = useState(true);
  const [videoMeta, setVideoMeta] = useState({ w: 0, h: 0 });
  const [trackingMode, setTrackingMode] = useState('dual'); // 'dual' | 'single (auto-degraded)'

  // Phase 5 auto-degrade refs
  const numHandsRef = useRef(2);
  const samplerStartedAtRef = useRef(null);
  const samplerDoneRef = useRef(false);
  const degradingRef = useRef(false);

  const supportsGetUserMedia =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    !!navigator.mediaDevices.getUserMedia;
  const supportsSpeech =
    typeof window !== 'undefined' && 'speechSynthesis' in window;

  // ---------------- MediaPipe init ----------------
  const initMediaPipe = useCallback(async () => {
    try {
      const filesetResolver = await FilesetResolver.forVisionTasks(WASM_BASE);
      const landmarker = await HandLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numHands: 2,
        minHandDetectionConfidence: 0.5,
        minHandPresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      landmarkerRef.current = landmarker;
      numHandsRef.current = 2;
      setMpReady(true);
      // eslint-disable-next-line no-console
      console.log('[SignVoice] Dual-hand tracking enabled (numHands: 2)');
    } catch (err) {
      console.error('[SignVoice] MediaPipe init failed:', err);
      setMpError(err?.message || 'MediaPipe failed to load');
    }
  }, []);

  // Phase 5 — re-create landmarker with numHands:1 after low-FPS measurement.
  const degradeToSingleHand = useCallback(async (measuredFps) => {
    if (degradingRef.current || numHandsRef.current === 1) return;
    degradingRef.current = true;
    try {
      try { landmarkerRef.current?.close(); } catch { /* noop */ }
      landmarkerRef.current = null;
      const filesetResolver = await FilesetResolver.forVisionTasks(WASM_BASE);
      const landmarker = await HandLandmarker.createFromOptions(filesetResolver, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
        runningMode: 'VIDEO',
        numHands: 1,
        minHandDetectionConfidence: 0.5,
        minHandPresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      landmarkerRef.current = landmarker;
      numHandsRef.current = 1;
      setTrackingMode('single');
      // eslint-disable-next-line no-console
      console.log(`[SignVoice] Dual-hand auto-degraded to single-hand. FPS=${measuredFps}`);
    } finally {
      degradingRef.current = false;
    }
  }, []);

  // ---------------- Camera ----------------
  const startCamera = useCallback(async () => {
    setCamError('');
    setCamStatus('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      await video.play();

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      setVideoMeta({ w: settings.width || video.videoWidth, h: settings.height || video.videoHeight });
      // eslint-disable-next-line no-console
      console.log(
        `[SignVoice] Camera resolution: ${settings.width}x${settings.height} @ ${
          settings.frameRate || '?'
        }fps`,
      );
      setCamStatus('granted');
    } catch (err) {
      console.error('[SignVoice] Camera error:', err);
      if (err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
        setCamStatus('denied');
        setCamError('Camera permission denied. Enable it in your browser settings.');
      } else if (err && err.name === 'NotFoundError') {
        setCamStatus('error');
        setCamError('No camera was found.');
      } else {
        setCamStatus('error');
        setCamError(err?.message || 'Could not access the camera.');
      }
    }
  }, []);

  // ---------------- Detection loop ----------------
  const loop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const landmarker = landmarkerRef.current;
    if (!video || !canvas || !landmarker || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(loop);
      return;
    }

    // Match canvas resolution to video for crisp drawing.
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }

    const now = performance.now();
    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const results = landmarker.detectForVideo(video, now);
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const handsRaw = results?.landmarks || [];
        if (handsRaw.length > 0) {
          // First hand violet (default), second hand cyan
          handsRaw.forEach((lm, i) => {
            if (i === 0) {
              drawHand(ctx, lm, canvas.width, canvas.height);
            } else {
              drawHand(ctx, lm, canvas.width, canvas.height, {
                lineColor: '#22D3EE',
                dotEdge: '#0EA5E9',
                dotColor: '#FFFFFF',
              });
            }
          });
          // Hand state from the FIRST hand (POC heuristic is single-hand)
          const shape = detectHandShape(handsRaw[0]);
          setHandState(shape);
        } else {
          setHandState('none');
        }

        // FPS tracking (rolling 3s window)
        const buf = fpsBufferRef.current;
        buf.push(now);
        const cutoff = now - 3000;
        while (buf.length && buf[0] < cutoff) buf.shift();
        let currentFps = 0;
        if (buf.length >= 2) {
          const dt = (buf[buf.length - 1] - buf[0]) / 1000;
          currentFps = dt > 0 ? Math.round((buf.length - 1) / dt) : 0;
          setFps(currentFps);
        }

        // Phase 5 — one-shot auto-degrade sampler
        if (!samplerDoneRef.current && numHandsRef.current === 2) {
          if (samplerStartedAtRef.current === null) {
            samplerStartedAtRef.current = now;
          } else if (now - samplerStartedAtRef.current >= 3000) {
            samplerDoneRef.current = true;
            if (currentFps > 0 && currentFps < 15) {
              degradeToSingleHand(currentFps);
            } else {
              // eslint-disable-next-line no-console
              console.log(`[SignVoice] Dual-hand confirmed. FPS=${currentFps}`);
            }
          }
        }
      } catch (err) {
        // detectForVideo can throw if timestamp doesn't progress — ignore briefly.
        // eslint-disable-next-line no-console
        console.debug('[SignVoice] detect skip:', err?.message);
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  }, [degradeToSingleHand]);

  // ---------------- Lifecycle ----------------
  useEffect(() => {
    if (!supportsSpeech) setTtsSupported(false);

    if (!initStartedRef.current) {
      initStartedRef.current = true;
      initMediaPipe();
      if (supportsGetUserMedia) startCamera();
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (landmarkerRef.current) {
        try {
          landmarkerRef.current.close();
        } catch {
          /* noop */
        }
        landmarkerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (camStatus === 'granted' && mpReady) {
      rafRef.current = requestAnimationFrame(loop);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [camStatus, mpReady, loop]);

  // ---------------- TTS ----------------
  const speakHello = useCallback(() => {
    if (!supportsSpeech) return;
    try {
      const synth = window.speechSynthesis;
      // Cancel any in-flight queue (helps iOS Safari)
      synth.cancel();
      const utter = new SpeechSynthesisUtterance('Hello, I am SignVoice');
      utter.lang = 'en-US';
      utter.rate = 1;
      utter.pitch = 1;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      synth.speak(utter);
    } catch (err) {
      console.error('[SignVoice] TTS error:', err);
      setSpeaking(false);
    }
  }, [supportsSpeech]);

  // ---------------- Derived UI strings ----------------
  const detectionLabel =
    handState === 'open'
      ? '✋ Hand detected'
      : handState === 'closed'
      ? '✊ Closed hand / other shape'
      : 'Waiting for hand...';

  const fpsAvg3s = fps;

  return (
    <div className="sv-shell relative" data-testid="poc-page">
      <div className="sv-grid-overlay" />

      {/* HEADER */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-8 sm:pt-10 pb-6 flex items-center justify-between">
        <div className="sv-logo" data-testid="sv-logo">
          <span className="sv-logo-mark">
            <Hand className="w-4 h-4 text-white" strokeWidth={2.5} />
          </span>
          <span>SignVoice</span>
          <span className="sv-phase ml-2" data-testid="phase-tag">PHASE 0 · POC</span>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <span className="sv-pill sv-pill-on" data-testid="header-status-pill">
            <Sparkles className="w-3 h-3" /> Web POC
          </span>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-24 sv-fade-up">
        {/* Title */}
        <section className="mb-8 sm:mb-10">
          <p className="sv-eyebrow mb-3">Technical de-risk</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="poc-heading">
            Hand tracking <span style={{ color: 'var(--sv-violet-bright)' }}>+ voice</span>,
            <br className="hidden sm:block" /> validated in the browser.
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg" style={{ color: 'var(--sv-text-dim)' }}>
            MediaPipe Hands running on your camera, with open-hand detection and a Web Speech API test.
            This is the isolated foundation of SignVoice before wiring the 3 agents.
          </p>
        </section>

        {/* CAMERA + CONTROLS */}
        <section className="grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-8 mb-10">
          {/* Camera card */}
          <div className="sv-card p-3 sm:p-4" data-testid="camera-card">
            <div className="sv-cam-frame">
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                data-testid="poc-video"
              />
              <canvas ref={canvasRef} data-testid="poc-canvas" />

              {/* HUD: FPS top right */}
              <div className="sv-hud sv-hud-tr">
                <div className="sv-fps" data-testid="fps-counter">
                  {fpsAvg3s} FPS
                </div>
              </div>

              {/* HUD: detection badge top left */}
              <div className="sv-hud sv-hud-tl">
                <div
                  className={`sv-detect-badge ${handState === 'open' ? 'sv-detect-on' : ''}`}
                  data-testid="detection-badge"
                >
                  <span>{detectionLabel}</span>
                  {handState === 'open' && (
                    <span className="sv-bars" aria-hidden="true" data-testid="hand-open-bars">
                      <span className="sv-bar" />
                      <span className="sv-bar" />
                      <span className="sv-bar" />
                      <span className="sv-bar" />
                    </span>
                  )}
                </div>
              </div>

              {/* Loading / denied overlays */}
              {camStatus !== 'granted' && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center text-center px-6"
                  style={{ background: 'rgba(10,15,37,0.85)', backdropFilter: 'blur(8px)' }}
                  data-testid="cam-overlay"
                >
                  <div className="max-w-sm">
                    {camStatus === 'requesting' && (
                      <>
                        <Activity className="w-8 h-8 mx-auto mb-3 animate-pulse" style={{ color: 'var(--sv-violet-bright)' }} />
                        <p className="font-semibold">Requesting camera access…</p>
                        <p className="text-sm mt-1" style={{ color: 'var(--sv-text-dim)' }}>
                          Accept the browser permission prompt.
                        </p>
                      </>
                    )}
                    {(camStatus === 'denied' || camStatus === 'error') && (
                      <>
                        <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: '#FCA5A5' }} />
                        <p className="font-semibold mb-1" data-testid="cam-error-title">
                          {camStatus === 'denied' ? 'Permission denied' : 'Could not start the camera'}
                        </p>
                        <p className="text-sm mb-4" style={{ color: 'var(--sv-text-dim)' }}>
                          {camError}
                        </p>
                        <button
                          className="sv-btn-ghost"
                          onClick={startCamera}
                          data-testid="cam-retry-btn"
                        >
                          <RefreshCw className="w-4 h-4" /> Retry
                        </button>
                      </>
                    )}
                    {camStatus === 'idle' && !supportsGetUserMedia && (
                      <>
                        <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: '#FCA5A5' }} />
                        <p className="font-semibold">Your browser does not support getUserMedia</p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4 px-1">
              <span className={`sv-pill ${mpReady ? 'sv-pill-ok' : 'sv-pill-warn'}`} data-testid="mp-ready-pill">
                {mpReady ? '● MediaPipe ready' : mpError ? '● MediaPipe error' : '● Loading MediaPipe…'}
              </span>
              <span className={`sv-pill ${camStatus === 'granted' ? 'sv-pill-ok' : 'sv-pill-warn'}`} data-testid="cam-pill">
                {camStatus === 'granted' ? '● Camera active' : '● Camera inactive'}
              </span>
              <span
                className={`sv-pill ${trackingMode === 'dual' ? 'sv-pill-on' : ''}`}
                data-testid="hands-pill"
                data-tracking-mode={trackingMode}
              >
                Hand tracking · {trackingMode === 'dual' ? 'Dual' : 'Single'}
              </span>
            </div>
          </div>

          {/* Voice + Diagnostics column */}
          <div className="flex flex-col gap-6">
            {/* TTS Card */}
            <div className="sv-card p-6" data-testid="tts-card">
              <p className="sv-eyebrow mb-2">Web Speech API</p>
              <h2 className="text-lg sm:text-xl font-semibold mb-1">Test the voice output</h2>
              <p className="text-sm mb-5" style={{ color: 'var(--sv-text-dim)' }}>
                Plays <span className="mono" style={{ color: 'var(--sv-text)' }}>“Hello, I am SignVoice”</span> using the
                browser's native voice. Works on desktop and mobile.
              </p>

              {!ttsSupported ? (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg"
                  style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.3)' }}
                  data-testid="tts-unsupported"
                >
                  <AlertCircle className="w-5 h-5 mt-0.5" style={{ color: '#FCA5A5' }} />
                  <div className="text-sm" style={{ color: '#FECACA' }}>
                    <strong>speechSynthesis not available.</strong> Use an up-to-date Chrome, Safari or Edge.
                  </div>
                </div>
              ) : (
                <button
                  className={`sv-btn-primary w-full ${speaking ? 'sv-speaking' : ''}`}
                  onClick={speakHello}
                  disabled={speaking}
                  data-testid="tts-button"
                >
                  <Mic className="w-5 h-5" />
                  {speaking ? '🟣 Speaking…' : 'Test voice'}
                </button>
              )}

              <p className="mt-4 text-xs leading-relaxed" style={{ color: 'var(--sv-text-muted)' }}>
                iOS Safari requires the first <span className="mono">speak()</span> call to come from a user click.
                This page respects that rule.
              </p>
            </div>

            {/* Diagnostics */}
            <div className="sv-card p-6" data-testid="diagnostics-card">
              <div className="flex items-center justify-between mb-3">
                <p className="sv-eyebrow">Diagnostics</p>
                <span className="sv-pill" data-testid="fps-avg-pill">FPS · {fpsAvg3s}</span>
              </div>

              <div className="sv-diag-row">
                <span className="sv-diag-key">User agent</span>
                <span className="sv-diag-val" data-testid="diag-ua">
                  {typeof navigator !== 'undefined' ? navigator.userAgent : '—'}
                </span>
              </div>
              <div className="sv-diag-row">
                <span className="sv-diag-key">getUserMedia</span>
                <span className="sv-diag-val" data-testid="diag-gum">
                  {supportsGetUserMedia ? '✅ supported' : '❌ no'}
                </span>
              </div>
              <div className="sv-diag-row">
                <span className="sv-diag-key">speechSynthesis</span>
                <span className="sv-diag-val" data-testid="diag-tts">
                  {supportsSpeech ? '✅ supported' : '❌ no'}
                </span>
              </div>
              <div className="sv-diag-row">
                <span className="sv-diag-key">MediaPipe</span>
                <span className="sv-diag-val" data-testid="diag-mp">
                  {mpReady ? '✅ loaded' : mpError ? `❌ ${mpError}` : '… loading'}
                </span>
              </div>
              <div className="sv-diag-row">
                <span className="sv-diag-key">Resolution</span>
                <span className="sv-diag-val" data-testid="diag-res">
                  {videoMeta.w && videoMeta.h ? `${videoMeta.w} × ${videoMeta.h}` : '—'}
                </span>
              </div>
              <div className="sv-diag-row">
                <span className="sv-diag-key">FPS (3s avg)</span>
                <span className="sv-diag-val" data-testid="diag-fps">{fpsAvg3s}</span>
              </div>
              <div className="sv-diag-row">
                <span className="sv-diag-key">Hand state</span>
                <span className="sv-diag-val" data-testid="diag-hand">{detectionLabel}</span>
              </div>
              <div className="sv-diag-row">
                <span className="sv-diag-key">Hand tracking mode</span>
                <span className="sv-diag-val" data-testid="diag-tracking-mode">
                  {trackingMode === 'dual' ? 'Dual' : 'Single (auto-degraded)'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <footer className="text-xs" style={{ color: 'var(--sv-text-muted)' }} data-testid="poc-footer">
          Isolated POC · No Mongo · No service worker · Ready for Phase 1.
        </footer>
      </main>
    </div>
  );
}
